import { useMemo, useState } from 'react';
import { ShieldCheck, UserPlus } from '@phosphor-icons/react';
import { ConfirmationModal } from '@/components/core';
import { useIsMobile } from '@/hooks/use-media-query';
import { Button } from '@/components/ui-kit/button';
import { Dialog, DialogTrigger } from '@/components/ui-kit/dialog';
import { useResendActivation } from '@/modules/auth/hooks/use-auth';
import { ProtectedFragment } from '@/state/store/auth/protected-fragment';
import { AddUser } from '../../components/add-profile/add-profile';
import { UserDetails } from '../../components/user-details/user-details';
import { UsersRolesToolbar } from '../../components/users-roles-toolbar/users-roles-toolbar';
import { UsersRolesTable } from '../../components/users-roles-table/users-roles-table';
import { UsersRolesPagination } from '../../components/users-roles-pagination/users-roles-pagination';
import { IamData } from '../../types/user.types';
import { useGetUsersQuery } from '../../hooks/use-iam';
import {
  getPrimaryRoleName,
  getUserAccountStatus,
  isOwnerRoleName,
  type UserAccountStatus,
} from '../../utils/users-roles.utils';

const MEMBERS_FETCH_SIZE = 200;
const DEFAULT_PAGE_SIZE = 25;

/**
 * Users & roles — organisation-scoped view of the IAM member list.
 * Fetches the full member set once and does search/filter/pagination client-side,
 * matching the Settings > Workspace design reference.
 */
export const UsersTablePage = () => {
  const isMobile = useIsMobile();
  const [openSheet, setOpenSheet] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IamData | null>(null);
  const [isResendActivationModalOpen, setIsResendActivationModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const { mutateAsync: resendActivation } = useResendActivation();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserAccountStatus | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [simulateError, setSimulateError] = useState(false);

  const { data, isLoading, isError } = useGetUsersQuery({
    page: 0,
    pageSize: MEMBERS_FETCH_SIZE,
  });

  const allUsers = useMemo(() => data?.data ?? [], [data?.data]);

  const roleOptions = useMemo(() => {
    const roles = new Set<string>();
    allUsers.forEach((user) => {
      const role = getPrimaryRoleName(user);
      if (role) roles.add(role);
    });
    return Array.from(roles).sort((a, b) => a.localeCompare(b));
  }, [allUsers]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return allUsers.filter((user) => {
      if (statusFilter !== 'all' && getUserAccountStatus(user) !== statusFilter) return false;
      if (roleFilter !== 'all' && getPrimaryRoleName(user) !== roleFilter) return false;
      if (query) {
        const haystack = `${user.firstName} ${user.lastName ?? ''} ${user.email}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [allUsers, search, statusFilter, roleFilter]);

  const pagedUsers = useMemo(
    () => filteredUsers.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [filteredUsers, pageIndex, pageSize]
  );

  const activeOwnerCount = useMemo(
    () =>
      allUsers.filter(
        (user) =>
          getUserAccountStatus(user) === 'active' && (user.roles ?? []).some(isOwnerRoleName)
      ).length,
    [allUsers]
  );

  const hasActiveFilters =
    search.trim().length > 0 || statusFilter !== 'all' || roleFilter !== 'all';

  const handleRowClick = (user: IamData) => {
    setSelectedUser(user);
    setOpenSheet(true);
  };

  const handleResendActivationClick = (user: IamData) => {
    setSelectedUser(user);
    setIsResendActivationModalOpen(true);
  };

  const handleConfirmActivation = async () => {
    if (!selectedUser) return;
    try {
      await resendActivation({ userId: selectedUser.itemId });
      setIsResendActivationModalOpen(false);
    } catch (error) {
      console.error('Failed to resend activation:', error);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPageIndex(0);
  };

  const handleStatusChange = (value: UserAccountStatus | 'all') => {
    setStatusFilter(value);
    setPageIndex(0);
  };

  const handleRoleChange = (value: string) => {
    setRoleFilter(value);
    setPageIndex(0);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPageIndex(0);
  };

  return (
    <div className="flex h-full min-h-0 w-full max-w-full flex-col gap-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-high-emphasis">Users &amp; roles</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Who has access to this organisation, and what each role can do.
          </p>
        </div>
        <Dialog open={isAddUserModalOpen} onOpenChange={setIsAddUserModalOpen}>
          <ProtectedFragment roles={['admin']}>
            <DialogTrigger asChild>
              <Button variant="default" className="flex items-center gap-1.5">
                <UserPlus size={16} />
                Invite user
              </Button>
            </DialogTrigger>
          </ProtectedFragment>
          {isAddUserModalOpen && <AddUser onClose={() => setIsAddUserModalOpen(false)} />}
        </Dialog>
      </header>

      <div className="flex items-start gap-2.5 rounded-lg bg-primary-50 px-3.5 py-2.5">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-primary-600" />
        <p className="text-sm text-primary-700">
          {activeOwnerCount} active owner{activeOwnerCount === 1 ? '' : 's'} in this organisation
          — at least one must remain active at all times.
        </p>
      </div>

      <UsersRolesToolbar
        search={search}
        onSearchChange={handleSearchChange}
        status={statusFilter}
        onStatusChange={handleStatusChange}
        role={roleFilter}
        onRoleChange={handleRoleChange}
        roleOptions={roleOptions}
        simulateError={simulateError}
        onSimulateErrorChange={setSimulateError}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card shadow-e1">
        <div className="min-h-0 flex-1 overflow-auto">
          <UsersRolesTable
            users={pagedUsers}
            isLoading={isLoading}
            isError={isError || simulateError}
            hasActiveFilters={hasActiveFilters}
            onRowClick={handleRowClick}
            onResendActivation={handleResendActivationClick}
          />
        </div>

        <UsersRolesPagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalCount={filteredUsers.length}
          onPageIndexChange={setPageIndex}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      {!isMobile && (
        <UserDetails open={openSheet} onOpenChange={setOpenSheet} selectedUser={selectedUser} />
      )}

      <ConfirmationModal
        open={isResendActivationModalOpen}
        onOpenChange={setIsResendActivationModalOpen}
        title="Activate this user?"
        description="Resending activation will restore this user's access."
        onConfirm={handleConfirmActivation}
      />
    </div>
  );
};
